import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { sendFirstSource } from "@/lib/firstSource";
import { useViewAs } from '@/hooks/useViewAs';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAdmin: boolean;
  isRealAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const checkAdminRoleAsync = async (userId: string) => {
      try {
        const { data } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'admin')
          .maybeSingle();
        if (isMounted) setIsAdmin(!!data);
      } catch (error) {
        console.error('Error checking admin role:', error);
        if (isMounted) setIsAdmin(false);
      }
    };

    const triggerGeoLocate = (userId: string) => {
      // Fire IP-based location capture once per user/browser, on any page after sign-in.
      // Server skips if the user already set a manual location.
      try {
        const KEY = 'community_geo_attempted_v1';
        const cached = JSON.parse(localStorage.getItem(KEY) || '{}');
        if (cached[userId]) return;
        cached[userId] = Date.now();
        localStorage.setItem(KEY, JSON.stringify(cached));
      } catch {}
      supabase.functions.invoke('geo-locate-user', { body: {} }).catch(() => {});
    };

    // Listener for ONGOING auth changes (does NOT control isLoading)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!isMounted) return;
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Fire and forget - don't await, don't set loading here
        if (currentSession?.user) {
          checkAdminRoleAsync(currentSession.user.id);
          triggerGeoLocate(currentSession.user.id);
          sendFirstSource(currentSession.access_token, currentSession.user.created_at);
        } else {
          setIsAdmin(false);
        }
      }
    );

    // INITIAL load (controls isLoading) - await everything before setting loading false
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        if (!isMounted) return;

        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        // Fetch role BEFORE setting loading false
        if (initialSession?.user) {
          await checkAdminRoleAsync(initialSession.user.id);
          triggerGeoLocate(initialSession.user.id);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // checkAdminRole is now defined inside useEffect as checkAdminRoleAsync

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  return (
    <InnerAuthContext.Provider
      value={{ user, session, isLoading, isRealAdmin: isAdmin, signIn, signOut, resetPassword }}
    >
      <AuthEffectiveBridge>{children}</AuthEffectiveBridge>
    </InnerAuthContext.Provider>
  );
}

// Internal context that holds the *real* auth state without view-as gating.
interface InnerAuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isRealAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}
const InnerAuthContext = createContext<InnerAuthContextType | undefined>(undefined);

// Bridges the inner real-auth context to the public AuthContext, applying the
// admin view-as override so consumers see the effective (downgraded) admin flag.
function AuthEffectiveBridge({ children }: { children: ReactNode }) {
  const inner = useContext(InnerAuthContext)!;
  const { role } = useViewAs();
  const effectiveIsAdmin = inner.isRealAdmin && role === 'auto';
  return (
    <AuthContext.Provider
      value={{
        user: inner.user,
        session: inner.session,
        isLoading: inner.isLoading,
        isAdmin: effectiveIsAdmin,
        isRealAdmin: inner.isRealAdmin,
        signIn: inner.signIn,
        signOut: inner.signOut,
        resetPassword: inner.resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
